import { useState, useEffect, useCallback } from 'react';
import { Link, useSearchParams, useNavigate } from 'react-router-dom';
import ReviewBadge, { useReviewBadges, previewToken } from '../../components/ReviewBadge';
import axios from 'axios';
import { toast } from 'sonner';
import { Search, Loader2, ChevronLeft, ChevronRight, ChevronDown, ShieldCheck, SlidersHorizontal, X, Package, Play, ExternalLink, Award, Check, Minus, Mic, ArrowRight, Star } from 'lucide-react';
import { useCompare, MAX_COMPARE } from '../../hooks/useCompare';
import { TrustBadge } from '../../components/TrustBadge';
import { LogoOrMark } from '../../components/Lettermark';
import { RecentlyViewed } from '../../components/RecentlyViewed';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../components/ui/dialog';
import { ShareButton } from '../../components/ShareButton';
import { ORIGIN, setSocialPreview } from '../../lib/seo';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const STATUS_LABEL = { live: 'Live', onboarding: 'Coming soon', piloting: 'Piloting', in_development: 'In development', concept: 'Concept' };
const STAGE_LABEL = { startup: 'Startup', established: 'Established', unknown: 'Unknown' };
const READINESS_LABEL = { in_development: 'In Development', pilot: 'In Pilot', available: 'Live' };

const StageChip = ({ stage, testid }) => (stage && stage !== 'unknown'
  ? <span className="text-[10px] font-semibold text-indigo-700 bg-indigo-50 border border-indigo-200 px-2 py-0.5 rounded-full" data-testid={testid}>{STAGE_LABEL[stage]}</span>
  : null);
// Market readiness (per product). Unpaid/unclaimed products read "Unknown".
const ReadinessChip = ({ availability, claimed, testid }) => {
  if (claimed === false) return <span className="text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full" data-testid={testid}>Unknown</span>;
  if (!availability || availability === 'available') return null;
  const cls = availability === 'pilot' ? 'text-teal-700 bg-teal-50 border-teal-200' : 'text-amber-700 bg-amber-50 border-amber-200';
  return <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${cls}`} data-testid={testid}>{READINESS_LABEL[availability]}</span>;
};
// Assurance (per product): green when independently assured, else subtle "Self-asserted".
const AssuranceChip = ({ assurance, testid }) => (assurance === 'independently_assured'
  ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-emerald-700 bg-emerald-50 border border-emerald-200 px-2 py-0.5 rounded-full" data-testid={testid}><ShieldCheck className="w-3 h-3" /> Independently Assured</span>
  : <span className="text-[10px] font-semibold text-gray-500 bg-gray-50 border border-gray-200 px-2 py-0.5 rounded-full" data-testid={testid}>Self-asserted</span>);
const ClaimChip = ({ claimed, testid }) => (
  <span className={`text-[10px] font-semibold px-2 py-0.5 rounded-full border ${claimed ? 'text-emerald-700 bg-emerald-50 border-emerald-200' : 'text-gray-500 bg-gray-50 border-gray-200'}`} data-testid={testid}>{claimed ? 'Claimed' : 'Unclaimed'}</span>
);

const ytThumb = (url) => {
  if (!url) return null;
  const m = url.match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  return m ? `https://img.youtube.com/vi/${m[1]}/hqdefault.jpg` : null;
};
const ytEmbed = (url) => {
  const m = (url || '').match(/(?:youtube\.com\/embed\/|youtu\.be\/|youtube\.com\/watch\?v=)([\w-]+)/);
  return m ? `https://www.youtube.com/embed/${m[1]}?autoplay=1&rel=0` : null;
};

const toggleIn = (arr, v) => (arr.includes(v) ? arr.filter((x) => x !== v) : [...arr, v]);
const kebab = (s) => s.replace(/[^a-zA-Z0-9]+/g, '-').toLowerCase().replace(/^-|-$/g, '');

const SupplierCard = ({ s, selected, onToggleCompare, onPlayVideo, onListen }) => {
  const rb = useReviewBadges().suppliers[s.slug];
  const thumb = ytThumb(s.video);
  return (
  <div className="bg-white border border-[#E5E7EB] rounded-lg hover:border-[#00A79D] hover:shadow-sm transition-all flex flex-col overflow-hidden" data-testid={`supplier-card-${s.slug}`}>
    <Link to={`/suppliers/${s.slug}`} className="flex flex-col flex-1">
      {thumb && (
        <div
          className="relative aspect-video bg-gray-900 overflow-hidden group cursor-pointer"
          data-testid={`supplier-video-thumb-${s.slug}`}
          role="button"
          onClick={(e) => { e.preventDefault(); e.stopPropagation(); onPlayVideo(s.video); }}
        >
          <img src={thumb} alt={`${s.name} video`} className="w-full h-full object-cover opacity-90 group-hover:opacity-100 transition-opacity" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
          <span className="absolute inset-0 flex items-center justify-center">
            <span className="w-11 h-11 rounded-full bg-white/90 flex items-center justify-center shadow-md group-hover:scale-105 transition-transform">
              <Play className="w-5 h-5 text-[#00A79D] fill-[#00A79D] ml-0.5" />
            </span>
          </span>
        </div>
      )}
      <div className="p-5 flex flex-col flex-1">
      <div className="flex items-start justify-between mb-3 gap-2">
        <LogoOrMark logo={s.logo} name={s.name} imgClassName="h-9 w-auto max-w-[140px] object-contain" markClassName="h-9 w-9 text-sm rounded-md" />
        <div className="flex flex-wrap items-center justify-end gap-1 shrink-0">
          {s.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full" data-testid={`supplier-featured-${s.slug}`}><Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Featured</span>}
          {s.tier && s.tier !== 'dormant'
            ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-[#1A3A5F] px-2 py-0.5 rounded-full" data-testid={`supplier-tier-${s.slug}`}><ShieldCheck className="w-3 h-3" /> {s.tier_label}</span>
            : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full" data-testid={`supplier-tier-${s.slug}`}>Dormant</span>}
          <StageChip stage={s.stage} testid={`supplier-stage-${s.slug}`} />
          <ReviewBadge badge={rb} small testid={`supplier-ctc-reviewed-${s.slug}`} />
        </div>
      </div>
      <h3 className="font-semibold text-gray-900">{s.name}</h3>
      {s.strength && s.strength.label && (
        <span className={`inline-flex items-center gap-1 self-start text-[10px] font-bold px-2 py-0.5 rounded-full mt-1.5 ${s.strength.band === 'specialist' ? 'text-white bg-[#1A3A5F]' : 'text-gray-600 bg-gray-100'}`} data-testid={`strength-${s.slug}`}>
          {s.strength.band === 'specialist' && <Award className="w-3 h-3" />} {s.strength.label}
        </span>
      )}
      {s.tagline && <p className="text-sm text-gray-500 mt-1 line-clamp-2 flex-1">{s.tagline}</p>}
      {Array.isArray(s.characteristics) && s.characteristics.length > 0 && (
        <div className="flex flex-wrap gap-x-3 gap-y-1 mt-2.5" data-testid={`characteristics-${s.slug}`}>
          {s.characteristics.slice(0, 6).map((ch) => (
            <span key={ch.label} className={`inline-flex items-center gap-1 text-[11px] ${ch.have ? 'text-emerald-700' : 'text-gray-300'}`}>
              {ch.have ? <Check className="w-3 h-3" /> : <Minus className="w-3 h-3" />} {ch.label}
            </span>
          ))}
        </div>
      )}
      <div className="flex flex-wrap gap-1 mt-3">
        {(s.categories || []).slice(0, 3).map((c) => <span key={c} className="text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{c}</span>)}
        {s.nhs_assured && <span className="text-[11px] text-blue-700 bg-blue-50 border border-blue-200 px-2 py-0.5 rounded">NHS Assured</span>}
        {s.podcast
          ? <span role="button" tabIndex={0} onClick={(e) => { e.preventDefault(); e.stopPropagation(); onListen(s.podcast); }} onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); e.stopPropagation(); onListen(s.podcast); } }} className="inline-flex items-center gap-1 text-[11px] text-[#1A3A5F] bg-[#1A3A5F]/5 border border-[#1A3A5F]/20 px-2 py-0.5 rounded cursor-pointer hover:bg-[#1A3A5F]/10 transition-colors" data-testid={`supplier-podcast-listen-${s.slug}`}><Play className="w-3 h-3 fill-[#1A3A5F]" /> Listen</span>
          : s.has_podcast && <span className="inline-flex items-center gap-1 text-[11px] text-[#1A3A5F] bg-[#1A3A5F]/5 border border-[#1A3A5F]/20 px-2 py-0.5 rounded" data-testid={`supplier-podcast-badge-${s.slug}`}><Mic className="w-3 h-3" /> Podcast</span>}
        {s.product_count > 0 && <span className="text-[11px] text-[#00A79D] bg-[#00A79D]/5 px-2 py-0.5 rounded" data-testid={`supplier-products-${s.slug}`}>{s.product_count} product{s.product_count > 1 ? 's' : ''}</span>}
        {s.trust && s.trust.tier !== 'unverified' && <span data-testid={`supplier-trust-${s.slug}`}><TrustBadge trust={s.trust} compact /></span>}
        {s.status && s.status !== 'live' && <span className="text-[11px] text-amber-700 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">{STATUS_LABEL[s.status] || s.status}</span>}
      </div>
      {(s.setting_sizes || []).length > 0 && (
        <div className="flex flex-wrap gap-1 mt-1.5" data-testid={`supplier-sizes-${s.slug}`}>
          {s.setting_sizes.map((c) => <span key={c} className="text-[11px] font-medium text-indigo-700 bg-indigo-50 border border-indigo-100 px-2 py-0.5 rounded">{c}</span>)}
        </div>
      )}
      </div>
    </Link>
    <label className="flex items-center gap-1.5 text-xs font-medium text-gray-500 cursor-pointer border-t border-[#F1F1F4] px-5 py-2.5 hover:text-[#00A79D]" data-testid={`compare-toggle-${s.slug}`}>
      <input type="checkbox" checked={selected} onChange={() => onToggleCompare(s.slug)} className="accent-[#00A79D]" />
      Compare
    </label>
    {s.claimed === false && (
      <Link to={`/claim/${s.slug}`} onClick={(e) => e.stopPropagation()}
        className="flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] border-t border-[#F1F1F4] px-5 py-2.5 hover:bg-[#00A79D]/5 transition-colors"
        data-testid={`supplier-claim-${s.slug}`}>
        <Award className="w-3.5 h-3.5" /> Is this you? Claim this listing
      </Link>
    )}
  </div>
  );
};

const ProductCard = ({ p }) => {
  const navigate = useNavigate();
  const rb = useReviewBadges().products[p.slug];
  return (
  <Link to={`/products/${p.slug}`} className={`bg-white border rounded-lg hover:shadow-sm transition-all flex flex-col overflow-hidden ${p.featured ? 'border-amber-300 ring-1 ring-amber-200 hover:border-amber-400' : 'border-[#E5E7EB] hover:border-[#00A79D]'}`} data-testid={`product-card-${p.slug}`}>
    {p.image && (
      <div className="aspect-video bg-[#F1F5F4] overflow-hidden">
        <img src={p.image} alt={p.name} className="w-full h-full object-cover" onError={(e) => { e.target.parentElement.style.display = 'none'; }} />
      </div>
    )}
    <div className="p-5 flex flex-col flex-1">
      <div className="flex flex-wrap items-center gap-1.5 mb-2">
        {p.featured && <span className="inline-flex items-center gap-1 text-[10px] font-bold text-amber-700 bg-amber-50 border border-amber-300 px-2 py-0.5 rounded-full" data-testid={`product-featured-${p.slug}`}><Star className="w-3 h-3 fill-amber-400 text-amber-500" /> Featured</span>}
        <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-[#00857D] bg-[#00A79D]/10 px-2 py-0.5 rounded"><Package className="w-3 h-3" /> Product</span>
        {p.tier_label && p.tier_label !== 'Dormant'
          ? <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-white bg-[#1A3A5F] px-2 py-0.5 rounded-full"><ShieldCheck className="w-3 h-3" /> {p.tier_label}</span>
          : <span className="inline-flex items-center gap-1 text-[10px] font-semibold text-gray-500 bg-gray-100 border border-gray-200 px-2 py-0.5 rounded-full">Dormant</span>}
        <ClaimChip claimed={p.claimed} testid={`product-claim-${p.slug}`} />
        <ReadinessChip availability={p.availability} claimed={p.claimed} testid={`product-readiness-${p.slug}`} />
        <AssuranceChip assurance={p.assurance} testid={`product-assurance-${p.slug}`} />
        <StageChip stage={p.stage} testid={`product-stage-${p.slug}`} />
        <ReviewBadge badge={rb} small testid={`product-ctc-reviewed-${p.slug}`} />
      </div>
      <h3 className="font-semibold text-gray-900">{p.name}</h3>
      <p className="text-xs text-gray-500 mt-0.5">by {p.supplier_name}</p>
      {p.description && <p className="text-sm text-gray-500 mt-2 line-clamp-2 flex-1">{p.description}</p>}
      <div className="flex flex-wrap gap-1 mt-3">
        {(p.categories || []).slice(0, 3).map((c) => <span key={c} className="text-[11px] text-gray-600 bg-gray-100 px-2 py-0.5 rounded">{c}</span>)}
      </div>
    </div>
    {p.claimed === false && p.supplier_slug && (
      <button type="button"
        onClick={(e) => { e.preventDefault(); e.stopPropagation(); navigate(`/claim/${p.supplier_slug}`); }}
        className="flex items-center gap-1.5 text-xs font-semibold text-[#00A79D] border-t border-[#F1F1F4] px-5 py-2.5 hover:bg-[#00A79D]/5 transition-colors mt-auto"
        data-testid={`product-claim-cta-${p.slug}`}>
        <Award className="w-3.5 h-3.5" /> Is this your product? Claim this listing
      </button>
    )}
  </Link>
  );
};

const CheckRow = ({ label, checked, onChange, count, testid }) => (
  <label className="flex items-center gap-2 py-1.5 cursor-pointer group" data-testid={testid}>
    <input type="checkbox" checked={checked} onChange={onChange} className="accent-[#00A79D] w-4 h-4 shrink-0" />
    <span className={`text-sm ${checked ? 'text-[#1A3A5F] font-medium' : 'text-gray-600'} group-hover:text-[#00857D]`}>{label}</span>
    {typeof count === 'number' && <span className="text-xs text-gray-400 ml-auto">{count}</span>}
  </label>
);

const Section = ({ title, children, defaultOpen = true }) => {
  const [open, setOpen] = useState(defaultOpen);
  return (
    <div className="border-b border-[#E5E7EB] py-4">
      <button onClick={() => setOpen(!open)} className="flex items-center justify-between w-full text-left">
        <span className="text-sm font-bold text-[#1A3A5F] uppercase tracking-wide">{title}</span>
        <span className="w-6 h-6 rounded-full border border-[#E5E7EB] flex items-center justify-center hover:border-[#00A79D]"><ChevronDown className={`w-4 h-4 text-[#00857D] transition-transform ${open ? 'rotate-180' : ''}`} /></span>
      </button>
      {open && <div className="mt-2">{children}</div>}
    </div>
  );
};

const Browse = () => {
  const [sp] = useSearchParams();
  const [meta, setMeta] = useState({ categories: [], product_groups: [], care_types: [], resident_types: [], specialisms: [], market_types: [], total_suppliers: 0, total_products: 0 });
  const [mode, setMode] = useState(() => (sp.get('mode') === 'products' ? 'products' : 'suppliers'));
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [banded, setBanded] = useState(false);
  const [bandCategory, setBandCategory] = useState(null);
  const [page, setPage] = useState(1);
  const pageSize = 24;
  const [loading, setLoading] = useState(true);
  const [mobileFilters, setMobileFilters] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [podcastEp, setPodcastEp] = useState(null);

  const [claimBanner, setClaimBanner] = useState(sp.get('claim') === '1');
  const [search, setSearch] = useState(() => sp.get('search') || '');
  const [categories, setCategories] = useState(() => { const c = sp.get('category'); return c ? [c] : []; });
  const [careTypes, setCareTypes] = useState([]);
  const [residentTypes, setResidentTypes] = useState([]);
  const [specialisms, setSpecialisms] = useState(() => { const s = sp.get('specialism'); return s ? s.split(',') : []; });
  const [marketTypes, setMarketTypes] = useState([]);
  const [mentalHealth, setMentalHealth] = useState(false);
  const [nhsAssured, setNhsAssured] = useState(false);
  const [hasPodcast, setHasPodcast] = useState(() => sp.get('podcast') === '1');
  const [stages, setStages] = useState(() => { const s = sp.get('stage'); return s ? s.split(',') : []; });
  const [availability, setAvailability] = useState([]);
  const [settingSizes, setSettingSizes] = useState(() => { const s = sp.get('setting'); return s ? s.split(',') : []; });
  const [sort, setSort] = useState('default');
  const [reviewedOnly, setReviewedOnly] = useState(() => sp.get('reviewed') === '1');
  const rbAll = useReviewBadges();
  const registerOn = Object.keys(rbAll.suppliers).length + Object.keys(rbAll.products).length > 0;
  const { isSelected, toggle } = useCompare();

  const onToggleCompare = (slug) => { if (!toggle(slug)) toast.error(`You can compare up to ${MAX_COMPARE} suppliers`); };
  const resetPage = () => setPage(1);

  useEffect(() => { axios.get(`${API}/public/meta`).then((r) => setMeta(r.data)).catch(() => {}); }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: pageSize };
      if (search) params.search = search;
      if (categories.length) params.category = categories.join(',');
      if (careTypes.length) params.care_type = careTypes.join(',');
      if (residentTypes.length) params.resident_type = residentTypes.join(',');
      if (specialisms.length) params.specialism = specialisms.join(',');
      if (marketTypes.length) params.market_type = marketTypes.join(',');
      if (mentalHealth) params.mental_health = true;
      if (nhsAssured) params.nhs_assured = true;
      if (hasPodcast) params.has_podcast = true;
      if (stages.length) params.stage = stages.join(',');
      if (availability.length) params.availability = availability.join(',');
      if (settingSizes.length) params.setting = settingSizes.join(',');
      if (sort !== 'default') params.sort = sort;
      if (reviewedOnly && registerOn) { params.reviewed = true; const t = previewToken(); if (t) params.rr_preview = t; }
      const url = mode === 'products' ? `${API}/public/products` : `${API}/public/suppliers`;
      const { data } = await axios.get(url, { params });
      setItems(data.items || []); setTotal(data.total || 0);
      setBanded(!!data.banded); setBandCategory(data.category || null);
    } finally { setLoading(false); }
  }, [mode, page, search, categories, careTypes, residentTypes, specialisms, marketTypes, mentalHealth, nhsAssured, hasPodcast, stages, availability, settingSizes, sort, reviewedOnly, registerOn]);

  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeCount = categories.length + careTypes.length + residentTypes.length + specialisms.length + marketTypes.length + (mentalHealth ? 1 : 0) + (nhsAssured ? 1 : 0) + (hasPodcast ? 1 : 0) + stages.length + availability.length + settingSizes.length + (reviewedOnly && registerOn ? 1 : 0);

  // Tailored share link/preview for the active category (or care setting) filter.
  const shareParams = (() => {
    const p = new URLSearchParams();
    if (categories[0]) p.set('category', categories[0]);
    if (mode === 'products') p.set('mode', 'products');
    if (!categories[0] && settingSizes[0]) p.set('setting', settingSizes[0]);
    return p.toString();
  })();
  const shareQs = shareParams ? `?${shareParams}` : '';
  const canShareFilter = !!(categories[0] || settingSizes[0]);

  useEffect(() => {
    if (!canShareFilter) return;
    const label = categories[0] || settingSizes[0];
    setSocialPreview({
      title: `${label} | Care Tech Compare`,
      image: `${ORIGIN}/api/public/share/browse/image.jpg${shareQs}`,
      url: `${ORIGIN}/browse${shareQs}`,
    });
  }, [canShareFilter, shareQs, categories, settingSizes]);

  const clearAll = () => {
    setCategories([]); setCareTypes([]); setResidentTypes([]); setMarketTypes([]);
    setMentalHealth(false); setNhsAssured(false); setHasPodcast(false); setStages([]); setAvailability([]); setSettingSizes([]); setSearch(''); setReviewedOnly(false); resetPage();
  };

  const Sidebar = () => (
    <div>
      <div className="flex items-center justify-between mb-2">
        <span className="text-sm font-bold text-[#1A3A5F]">Filters {activeCount > 0 && <span className="text-[#00A79D]">({activeCount})</span>}</span>
        {activeCount > 0 && <button onClick={clearAll} className="text-xs font-semibold text-[#00857D] hover:underline" data-testid="browse-clear">Clear all</button>}
      </div>

      <Section title="Product / Service Type">
        <div className="max-h-72 overflow-y-auto pr-1">
          {(meta.product_groups || []).map((g) => (
            <ProductGroup key={g.group} g={g} selected={categories} onToggle={(v) => { setCategories(toggleIn(categories, v)); resetPage(); }} />
          ))}
        </div>
      </Section>

      <Section title="Care Type">
        {meta.care_types.map((c) => (
          <CheckRow key={c} label={c} checked={careTypes.includes(c)} onChange={() => { setCareTypes(toggleIn(careTypes, c)); resetPage(); }} testid={`filter-caretype-${kebab(c)}`} />
        ))}
      </Section>

      <Section title="Resident Type">
        {meta.resident_types.map((c) => (
          <CheckRow key={c} label={c} checked={residentTypes.includes(c)} onChange={() => { setResidentTypes(toggleIn(residentTypes, c)); resetPage(); }} testid={`filter-residenttype-${kebab(c)}`} />
        ))}
      </Section>

      <Section title="Specialisms">
        {(meta.specialisms || []).map((c) => (
          <CheckRow key={c} label={c} checked={specialisms.includes(c)} onChange={() => { setSpecialisms(toggleIn(specialisms, c)); resetPage(); }} testid={`filter-specialism-${kebab(c)}`} />
        ))}
      </Section>

      <Section title="Assurance">
        {registerOn && <CheckRow label="CTC reviewed only" checked={reviewedOnly} onChange={() => { setReviewedOnly(!reviewedOnly); resetPage(); }} testid="filter-ctc-reviewed" />}
        <CheckRow label="NHS Digital Assured Supplier" checked={nhsAssured} onChange={() => { setNhsAssured(!nhsAssured); resetPage(); }} testid="filter-nhs" />
      </Section>

      <Section title="Company stage">
        {['startup', 'established', 'unknown'].map((s) => (
          <CheckRow key={s} label={STAGE_LABEL[s]} checked={stages.includes(s)} onChange={() => { setStages(toggleIn(stages, s)); resetPage(); }} testid={`filter-stage-${s}`} />
        ))}
      </Section>

      {mode === 'products' && (
        <Section title="Market readiness">
          {['available', 'pilot', 'in_development'].map((a) => (
            <CheckRow key={a} label={READINESS_LABEL[a]} checked={availability.includes(a)} onChange={() => { setAvailability(toggleIn(availability, a)); resetPage(); }} testid={`filter-readiness-${a}`} />
          ))}
        </Section>
      )}

      {(meta.care_setting_sizes || []).length > 0 && (
        <Section title="Care setting & size">
          {meta.care_setting_sizes.map((c) => (
            <CheckRow key={c} label={c} checked={settingSizes.includes(c)} onChange={() => { setSettingSizes(toggleIn(settingSizes, c)); resetPage(); }} testid={`filter-setting-${kebab(c)}`} />
          ))}
        </Section>
      )}

      {mode !== 'products' && (
        <Section title="Podcast">
          <CheckRow label="Has a podcast episode" checked={hasPodcast} onChange={() => { setHasPodcast(!hasPodcast); resetPage(); }} testid="filter-has-podcast" />
        </Section>
      )}

      <Section title="Market Type">
        {meta.market_types.map((c) => (
          <CheckRow key={c} label={c} checked={marketTypes.includes(c)} onChange={() => { setMarketTypes(toggleIn(marketTypes, c)); resetPage(); }} testid={`filter-market-${kebab(c)}`} />
        ))}
      </Section>
    </div>
  );

  return (
    <div data-testid="public-browse">
      <div className="bg-white border-b border-[#E5E7EB]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-9">
          <h1 className="font-heading text-3xl sm:text-4xl tracking-tight font-bold text-gray-900">Browse Care Tech</h1>
          <p className="text-gray-600 mt-2">{meta.total_suppliers.toLocaleString()} suppliers · {meta.total_products.toLocaleString()} products, from established platforms to emerging startups.</p>
          {/* Suppliers / Products toggle */}
          <div className="flex items-center justify-between gap-4 flex-wrap mt-5">
            <div className="inline-flex bg-[#F1F5F4] rounded-lg p-1" data-testid="browse-mode-toggle">
              <button onClick={() => { setMode('suppliers'); resetPage(); }} data-testid="browse-mode-suppliers"
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${mode === 'suppliers' ? 'bg-white text-[#00857D] shadow-sm' : 'text-gray-500'}`}>Suppliers</button>
              <button onClick={() => { setMode('products'); resetPage(); }} data-testid="browse-mode-products"
                className={`px-5 py-2 text-sm font-semibold rounded-md transition-colors ${mode === 'products' ? 'bg-white text-[#00857D] shadow-sm' : 'text-gray-500'}`}>Products</button>
            </div>
            {canShareFilter && (
              <ShareButton
                shareUrl={`${API}/public/share/browse${shareQs}`}
                title={`${categories[0] || settingSizes[0]} | Care Tech Compare`}
                label={`Share ${categories[0] || settingSizes[0]}`}
                testid="browse-share-btn"
                className="inline-flex items-center justify-center gap-1.5 border border-[#E5E7EB] hover:border-[#00A79D] text-gray-700 hover:text-[#00857D] font-semibold text-sm px-4 py-2 rounded-md transition-colors"
              />
            )}
          </div>
        </div>
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 flex gap-8">
        {/* Desktop sidebar */}
        <aside className="hidden lg:block w-64 shrink-0" data-testid="browse-filters">
          <div className="sticky top-24"><Sidebar /></div>
        </aside>

        <div className="flex-1 min-w-0">
          {claimBanner && (
            <div className="mb-6 bg-[#1A3A5F] text-white rounded-lg p-4 sm:p-5 flex items-start gap-3" data-testid="browse-claim-banner">
              <ShieldCheck className="w-5 h-5 text-[#2fd0c6] shrink-0 mt-0.5" />
              <div className="flex-1 min-w-0">
                <p className="font-semibold">Is your company listed? Find &amp; claim it.</p>
                <p className="text-sm text-white/70 mt-0.5">Every supplier already has a free listing. Search below, open your company page and hit <span className="font-semibold text-white">"Claim &amp; get Confirmed"</span> to manage it.</p>
              </div>
              <button onClick={() => setClaimBanner(false)} className="text-white/60 hover:text-white shrink-0" aria-label="Dismiss" data-testid="browse-claim-banner-dismiss"><X className="w-4 h-4" /></button>
            </div>
          )}
          <RecentlyViewed className="mb-6" />
          {mode !== 'products' && careTypes.length === 1 && categories.length === 0 && ((meta.popular_by_setting || {})[careTypes[0]] || []).length > 0 && (
            <div className="mb-6 bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid="popular-in-setting">
              <p className="text-sm font-semibold text-gray-700 mb-2.5">Popular in {careTypes[0]}</p>
              <div className="flex flex-wrap gap-2">
                {meta.popular_by_setting[careTypes[0]].map((c) => (
                  <button key={c} onClick={() => { setCategories([c]); resetPage(); }} className="text-sm font-medium text-[#00857D] bg-[#00A79D]/8 border border-[#00A79D]/30 rounded-full px-3 py-1.5 hover:bg-[#00A79D]/15 transition-colors" data-testid={`popular-cat-${kebab(c)}`}>{c}</button>
                ))}
              </div>
            </div>
          )}
          <div className="flex flex-wrap gap-3 items-center mb-6">
            <div className="relative flex-1 min-w-[220px]">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
              <input className="w-full pl-9 pr-3 py-2.5 border border-[#E5E7EB] rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-[#00A79D]/40"
                placeholder={mode === 'products' ? 'Search products…' : 'Search suppliers, products, categories…'} value={search} onChange={(e) => { resetPage(); setSearch(e.target.value); }} data-testid="browse-search" />
            </div>
            <button onClick={() => setMobileFilters(true)} className="lg:hidden inline-flex items-center gap-1.5 text-sm font-medium px-4 py-2.5 rounded-md border border-[#E5E7EB]" data-testid="browse-mobile-filter-btn">
              <SlidersHorizontal className="w-4 h-4" /> Filters {activeCount > 0 && `(${activeCount})`}
            </button>
            <select className="border border-[#E5E7EB] rounded-md text-sm py-2.5 px-3 bg-white" value={sort} onChange={(e) => { resetPage(); setSort(e.target.value); }} data-testid="browse-sort">
              <option value="default">Sort: Featured</option>
              <option value="az">A to Z</option>
              <option value="za">Z to A</option>
            </select>
          </div>

          {loading ? (
            <div className="py-20 text-center text-gray-400"><Loader2 className="w-6 h-6 animate-spin inline" /></div>
          ) : items.length === 0 ? (
            <div className="py-16 text-center" data-testid="browse-empty">
              <p className="text-gray-400">No {mode} match your {search.trim() ? 'search' : 'filters'}.</p>
              {!search.trim() && mode !== 'products' && <p className="text-sm text-gray-500 mt-2" data-testid="browse-dormant-hint">Filters show Confirmed suppliers. Looking for a specific company? Search by its name above.</p>}
              {mode !== 'products' && (
                <div className="max-w-md mx-auto mt-6 bg-white border border-[#E5E7EB] rounded-lg p-6" data-testid="browse-no-match-claim">
                  <p className="font-semibold text-gray-900">Can't find your company?</p>
                  <p className="text-sm text-gray-500 mt-1">If you're a supplier and you're not in the directory yet, create your listing with a Confirmed plan, or get in touch and our team will take a look.</p>
                  <Link to={`/for-suppliers${search.trim() ? `?company=${encodeURIComponent(search.trim())}` : ''}`} className="inline-flex items-center gap-1.5 bg-[#00A79D] hover:bg-[#00857D] text-white font-semibold text-sm px-5 py-2.5 rounded-md transition-colors mt-4" data-testid="browse-request-listing">Request a listing <ArrowRight className="w-4 h-4" /></Link>
                </div>
              )}
            </div>
          ) : banded && mode !== 'products' ? (
            (() => {
              const specialists = items.filter((s) => s.strength && s.strength.band === 'specialist');
              const also = items.filter((s) => !(s.strength && s.strength.band === 'specialist'));
              const Grid = ({ list }) => (
                <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4">
                  {list.map((s) => <SupplierCard key={s.slug} s={s} selected={isSelected(s.slug)} onToggleCompare={onToggleCompare} onPlayVideo={setVideoUrl} onListen={setPodcastEp} />)}
                </div>
              );
              return (
                <div className="space-y-8" data-testid="browse-grid">
                  {specialists.length > 0 && (
                    <div data-testid="band-specialists">
                      <div className="flex items-center gap-2 mb-3">
                        <Award className="w-4 h-4 text-[#1A3A5F]" />
                        <h2 className="font-heading text-lg font-bold text-gray-900">Specialists in {bandCategory}</h2>
                        <span className="text-xs text-gray-400">strongest in this area</span>
                      </div>
                      <Grid list={specialists} />
                    </div>
                  )}
                  {also.length > 0 && (
                    <div data-testid="band-also">
                      <h2 className="font-heading text-sm font-semibold text-gray-500 mb-3 uppercase tracking-wide">Also available</h2>
                      <Grid list={also} />
                    </div>
                  )}
                </div>
              );
            })()
          ) : (
            <div className="grid grid-cols-1 sm:grid-cols-2 xl:grid-cols-3 gap-4" data-testid="browse-grid">
              {mode === 'products'
                ? items.map((p) => <ProductCard key={p.slug} p={p} />)
                : items.map((s) => <SupplierCard key={s.slug} s={s} selected={isSelected(s.slug)} onToggleCompare={onToggleCompare} onPlayVideo={setVideoUrl} onListen={setPodcastEp} />)}
            </div>
          )}

          <div className="flex items-center justify-between mt-8">
            <span className="text-sm text-gray-500">{total.toLocaleString()} {mode}</span>
            <div className="flex items-center gap-2">
              <button disabled={page <= 1} onClick={() => setPage(page - 1)} className="p-2 border border-[#E5E7EB] rounded-md disabled:opacity-40" data-testid="browse-prev"><ChevronLeft className="w-4 h-4" /></button>
              <span className="text-sm">{page} / {totalPages}</span>
              <button disabled={page >= totalPages} onClick={() => setPage(page + 1)} className="p-2 border border-[#E5E7EB] rounded-md disabled:opacity-40" data-testid="browse-next"><ChevronRight className="w-4 h-4" /></button>
            </div>
          </div>
        </div>
      </div>

      {/* Mobile filter drawer */}
      {mobileFilters && (
        <div className="fixed inset-0 z-50 lg:hidden" data-testid="browse-mobile-drawer">
          <div className="absolute inset-0 bg-black/40" onClick={() => setMobileFilters(false)} />
          <div className="absolute right-0 top-0 h-full w-[85%] max-w-sm bg-white overflow-y-auto p-5">
            <div className="flex items-center justify-between mb-3">
              <span className="font-heading font-bold text-gray-900">Filters</span>
              <button onClick={() => setMobileFilters(false)} className="p-1.5" data-testid="browse-mobile-close"><X className="w-5 h-5" /></button>
            </div>
            <Sidebar />
            <button onClick={() => setMobileFilters(false)} className="w-full mt-5 bg-[#00A79D] text-white font-semibold py-2.5 rounded-md">Show {total} results</button>
          </div>
        </div>
      )}

      {/* Video lightbox */}
      <Dialog open={!!videoUrl} onOpenChange={(o) => { if (!o) setVideoUrl(null); }}>
        <DialogContent className="max-w-3xl p-0 overflow-hidden bg-black border-0" data-testid="browse-video-modal">
          {videoUrl && (
            <>
              <div className="aspect-video w-full">
                <iframe className="w-full h-full" src={ytEmbed(videoUrl)} title="Supplier video" allow="autoplay; encrypted-media; fullscreen" allowFullScreen />
              </div>
              <a href={videoUrl} target="_blank" rel="noreferrer" className="flex items-center justify-center gap-1.5 text-xs text-gray-300 hover:text-white py-2.5 bg-black">Watch on YouTube <ExternalLink className="w-3.5 h-3.5" /></a>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Podcast listen modal */}
      <Dialog open={!!podcastEp} onOpenChange={(o) => { if (!o) setPodcastEp(null); }}>
        <DialogContent className="max-w-lg" data-testid="browse-podcast-modal">
          {podcastEp && (
            <>
              <DialogHeader><DialogTitle className="text-base pr-6 flex items-center gap-2"><Mic className="w-4 h-4 text-[#00A79D]" /> {podcastEp.title}</DialogTitle></DialogHeader>
              {podcastEp.spotify_embed && (
                <iframe title={podcastEp.title} src={podcastEp.spotify_embed} width="100%" height="152" frameBorder="0" allow="clipboard-write; encrypted-media; fullscreen; picture-in-picture" loading="lazy" className="rounded-xl" />
              )}
              <div className="flex flex-wrap gap-2 mt-1">
                {podcastEp.spotify_url && <a href={podcastEp.spotify_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1DB954] hover:bg-[#1aa34a] text-white rounded-full px-4 py-2 transition-colors" data-testid="browse-podcast-spotify">Listen on Spotify</a>}
                {podcastEp.apple_url && <a href={podcastEp.apple_url} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1.5 text-xs font-semibold bg-[#1A3A5F] hover:bg-[#12293f] text-white rounded-full px-4 py-2 transition-colors" data-testid="browse-podcast-apple">Listen on Apple Podcasts</a>}
                <Link to={podcastEp.post_url} className="inline-flex items-center gap-1.5 text-xs font-semibold border border-[#E5E7EB] hover:border-[#00A79D] text-gray-700 rounded-full px-4 py-2 transition-colors" data-testid="browse-podcast-fullpage">Full episode page</Link>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
};

const ProductGroup = ({ g, selected, onToggle }) => {
  const [open, setOpen] = useState(false);
  const groupSelected = selected.includes(g.group);
  return (
    <div className="mb-1">
      <div className="flex items-center gap-2">
        <input type="checkbox" checked={groupSelected} onChange={() => onToggle(g.group)} className="accent-[#00A79D] w-4 h-4" data-testid={`filter-cat-${kebab(g.group)}`} />
        <button onClick={() => setOpen(!open)} className="flex items-center justify-between flex-1 text-left py-1 group" data-testid={`filter-group-toggle-${kebab(g.group)}`} aria-expanded={open}>
          <span className="text-sm font-semibold text-[#1A3A5F]">{g.group}</span>
          <span className={`inline-flex items-center gap-1 text-[11px] font-semibold rounded-full px-2 py-0.5 border transition-colors ${open ? 'bg-[#00A79D] text-[#ffffff] border-[#00A79D]' : 'text-[#00857D] border-[#00A79D]/40 bg-[#00A79D]/5 group-hover:bg-[#00A79D]/15'}`}>
            {open ? 'Hide' : `${g.items.length} types`}<ChevronDown className={`w-3.5 h-3.5 transition-transform ${open ? 'rotate-180' : ''}`} />
          </span>
        </button>
      </div>
      {open && (
        <div className="pl-6">
          {g.items.map((it) => (
            <label key={it} className="flex items-center gap-2 py-1 cursor-pointer" data-testid={`filter-cat-${kebab(it)}`}>
              <input type="checkbox" checked={selected.includes(it)} onChange={() => onToggle(it)} className="accent-[#00A79D] w-3.5 h-3.5" />
              <span className={`text-[13px] ${selected.includes(it) ? 'text-[#00857D] font-medium' : 'text-gray-600'}`}>{it}</span>
            </label>
          ))}
        </div>
      )}
    </div>
  );
};

export default Browse;
